/**
 * Operator UI preferences. Zustand owns this because it is read by components
 * across the tree and must survive a reload, while never belonging to the
 * server cache that TanStack Query manages.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { applyDocumentLocale, defaultLocale, isLocale, type Locale } from '../i18n';

export type Theme = 'light' | 'dark' | 'system';

interface PreferencesState {
  locale: Locale;
  theme: Theme;
  /** Collapsed state of the console's navigation rail. */
  navCollapsed: boolean;
  setLocale: (locale: Locale) => void;
  setTheme: (theme: Theme) => void;
  toggleNav: () => void;
}

export const usePreferences = create<PreferencesState>()(
  persist(
    (set) => ({
      locale: defaultLocale,
      theme: 'system',
      navCollapsed: false,
      setLocale: (locale) => {
        applyDocumentLocale(locale);
        set({ locale });
      },
      setTheme: (theme) => set({ theme }),
      toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),
    }),
    {
      name: 'rootsight.preferences',
      version: 1,
      // Reject a locale that was removed since the preference was stored.
      merge: (persisted, current) => {
        const saved = persisted as Partial<PreferencesState> | undefined;
        const locale = saved?.locale && isLocale(saved.locale) ? saved.locale : current.locale;
        return { ...current, ...saved, locale };
      },
    },
  ),
);

/** storedTheme reads the persisted theme before React mounts, so the page is
 * painted in the operator's mode from the first frame. */
export function storedTheme(): Theme {
  try {
    const raw = localStorage.getItem('rootsight.preferences');
    if (!raw) return 'system';
    const parsed = JSON.parse(raw) as { state?: { theme?: string } };
    const theme = parsed.state?.theme;
    return theme === 'light' || theme === 'dark' ? theme : 'system';
  } catch {
    return 'system';
  }
}

/** storedLocale reads the persisted locale before React mounts, so i18n can
 * initialize in the right language and avoid a flash of English. */
export function storedLocale(): Locale | undefined {
  try {
    const raw = localStorage.getItem('rootsight.preferences');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { state?: { locale?: string } };
    const locale = parsed.state?.locale;
    return locale && isLocale(locale) ? locale : undefined;
  } catch {
    // Private browsing or disabled storage: fall back to detection.
    return undefined;
  }
}
